// modify-update-assignment planner. Replaces the value-text bytes of an
// existing assignment in `UPDATE … SET col = value, …`. The target column
// (and the `=` itself) are preserved verbatim — caller can rename a column
// with a separate refactor.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Structural Edit Applicator — modify.

import type { PlannedByteEdit, StructuralEdit, UpdateAssignment } from '../types';

import type { EditCtx } from './applyStructuralEdit';
import { rangeFromOffsets } from '../../sql-grammar/document/lineIndex';
import type { ParseOptions } from '../../sql-grammar/model';
import { codeEndBeforeTrailingComments } from '../structure/scanner';

export function planUpdateAssignmentEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'modify-update-assignment' }>,
): readonly PlannedByteEdit[] {
  const assigns = ctx.structure.updateAssignments ?? [];
  if (edit.index < 0 || edit.index >= assigns.length) return [];
  const a = assigns[edit.index];
  const valueRange = findValueByteRange(ctx.source, a, ctx.options);
  if (!valueRange) return [];
  return [{
    range: rangeFromOffsets(ctx.lineIdx, valueRange.start, valueRange.end),
    replacement: edit.newValueText,
    rationale: 'modify-update-assignment',
  }];
}

/**
 * The value bytes of `col = value` — after the `=`, trimmed, and WITHOUT a
 * trailing comment (`1 --ar applied` → `1`), so replacing the value keeps the
 * comment. Shared by the UPDATE, MERGE-branch and conflict-tail planners.
 */
export function findValueByteRange(
  source: string,
  a: UpdateAssignment,
  options: ParseOptions,
): { start: number; end: number } | null {
  // Locate the `=` between target and assignment-end. The DML extractor builds
  // `valueText` from the trimmed bytes after `=`, so we re-derive that range.
  let eq = a.target.range.endOffset;
  while (eq < a.range.endOffset && source[eq] !== '=') eq++;
  if (eq >= a.range.endOffset) return null;
  let start = eq + 1;
  while (start < a.range.endOffset && /\s/.test(source[start])) start++;
  const end = codeEndBeforeTrailingComments(source, start, a.range.endOffset, options);
  if (end <= start) return null;
  return { start, end };
}
